import classNames from "classnames";
import { t } from "i18next";
import React, { useMemo, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";

import { JP_PREFECTURES } from "@repo/shopifyV2/const/address";
import type { ICaaCustomerAddress } from "@repo/shopifyV2/interface/customer-account";
import { type IJpZipAddress, searchJpAddressesByZip } from "@repo/shopifyV2/utils/address";

import Button from "@atoms/Button";
import Input from "@atoms/Input";
import Select from "@atoms/Select";

import AddressSuggestions from "@molecules/AddressSuggestions";

import useCustomerCaa from "@queries/useCustomerCaa";

interface IProps {
  address?: ICaaCustomerAddress;
  isDefaultAddress?: boolean;
  isEmptyAddress?: boolean;
  className?: string;
  onClickCancel?: () => void;
  onCompletedMutation?: () => void | Promise<void>;
}

interface IFormAddress {
  lastName: string;
  firstName: string;
  phoneNumber: string;
  zip: string;
  zoneCode: string;
  city: string;
  address1: string;
  address2: string;
  company: string;
}

interface IZipSuggestion extends IJpZipAddress {
  placeId: string;
  text: string;
  matches: { startOffset: number; endOffset: number }[];
}

// 한자, 히라가나, 가타카나(반복 기호 々〆〤ヶ, 장음 ー, 반각 가타카나 ｡-ﾟ 포함), 공백, 기호만 허용
const JAPANESE_REGEX =
  /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}｡-ﾟー々〆〤ヶ\s\-−‐―－～〜、。，．・：；？！＼／＿（）「」『』]+$/u;
// 住所는 번지(丁目·番地 등) 숫자가 들어가므로 반각·전각 숫자 허용
const JAPANESE_ADDRESS_REGEX =
  /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}｡-ﾟー々〆〤ヶ0-9０-９\s\-−‐―－～〜、。，．・：；？！＼／＿（）「」『』]+$/u;

export default function AddressFormJp({
  address,
  isDefaultAddress = false,
  isEmptyAddress = false,
  className,
  onClickCancel,
  onCompletedMutation,
}: IProps) {
  const { createCaaAddress, createCaaAddressLoading, editCaaAddress, editCaaAddressLoading } = useCustomerCaa();

  const isEdit = Boolean(address?.id);

  const [isProgress, setIsProgress] = useState(false);
  const [checkedDefaultAddress, setCheckedDefaultAddress] = useState(false);
  const [zipSuggestions, setZipSuggestions] = useState<IZipSuggestion[]>([]);
  const [isZipSuggestionsVisible, setIsZipSuggestionsVisible] = useState(false);

  // 연속 입력 시 이전 검색 결과가 덮어쓰지 않도록 마지막 입력값 기준으로만 반영
  const latestZipRef = useRef("");

  /** 1500001 → 150-0001 */
  const formatJpZip = (zip: string) => (zip.length > 3 ? `${zip.slice(0, 3)}-${zip.slice(3)}` : zip);

  const PREFECTURE_OPTIONS = JP_PREFECTURES.map(({ name }) => ({ name, value: name }));

  const defaultValues: IFormAddress = useMemo(
    () => ({
      lastName: address?.lastName || "",
      firstName: address?.firstName || "",
      phoneNumber: address?.phoneNumber || "",
      zip: address?.zip || "",
      // 기존 주소의 zoneCode가 코드(JP-13)로 저장돼 있어도 이름(東京都)으로 맞춤, 목록에 없으면 빈 값
      zoneCode: JP_PREFECTURES.find(({ code, name }) => code === address?.zoneCode || name === address?.zoneCode)?.name ?? "",
      city: address?.city || "",
      address1: address?.address1 || "",
      address2: address?.address2 || "",
      company: "",
    }),
    [address],
  );

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm<IFormAddress>({ defaultValues });

  const onChangeZip = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    latestZipRef.current = input;

    const results = await searchJpAddressesByZip(input);
    if (latestZipRef.current !== input) return;

    const digitsLength = input.replace(/\D/g, "").length;
    setZipSuggestions(
      results.map((item) => {
        const zip = formatJpZip(item.zip);
        return {
          ...item,
          placeId: item.zip,
          text: `${zip} ${item.prefecture}${item.city}${item.town}`,
          // 입력한 우편번호 부분 하이라이트 (4자리 이상이면 하이픈 포함)
          matches: [{ startOffset: 0, endOffset: formatJpZip("0".repeat(digitsLength)).length }],
        };
      }),
    );
    setIsZipSuggestionsVisible(true);
  };

  const onSelectZipSuggestion = (suggestion: IZipSuggestion) => () => {
    setValue("zip", formatJpZip(suggestion.zip), { shouldValidate: true });
    setValue("zoneCode", suggestion.prefecture, { shouldValidate: true });
    setValue("city", suggestion.city, { shouldValidate: true });
    setValue("address1", suggestion.town, { shouldValidate: Boolean(suggestion.town) });
    setZipSuggestions([]);
    setFocus("address1");
  };

  const onSubmit: SubmitHandler<IFormAddress> = async (formData) => {
    if (isProgress) return;

    setIsProgress(true);

    try {
      const input = { ...formData, territoryCode: "JP" };
      const defaultAddress = Boolean(checkedDefaultAddress || isEmptyAddress || isDefaultAddress);

      if (isEdit && address?.id) {
        const data = await editCaaAddress({ addressId: address.id, address: input, defaultAddress });
        if (data?.customerAddressUpdate.userErrors.length) return;
      } else {
        const data = await createCaaAddress({ address: input, defaultAddress });
        if (data?.customerAddressCreate.userErrors.length) return;
      }

      await onCompletedMutation?.();
      onClickCancel?.();
    } catch {
      // 요청 실패 토스트도 useCustomerQueryCaa에서 띄움
    } finally {
      setIsProgress(false);
    }
  };

  const isLoading = isProgress || createCaaAddressLoading || editCaaAddressLoading;

  return (
    <form className="flex h-[calc(100dvh-7.5rem)] flex-col" onSubmit={handleSubmit(onSubmit)}>
      <div className={classNames("scrollbar-hide min-h-0 flex-1 content-start items-start overflow-y-scroll", className)}>
        {/* 1. 성 (姓) */}
        <div className="col-span-3">
          <Input
            {...register("lastName", { required: true })}
            errorMessage={errors.lastName ? t(`error.necessary_lastName`) : ""}
            isError={Boolean(errors.lastName)}
            label={t("last_name")}
            placeholder={t("last_name")}
          />
        </div>
        {/* 2. 이름 (名) */}
        <div className="col-span-3">
          <Input
            {...register("firstName", { required: true })}
            errorMessage={errors.firstName ? t(`error.necessary_firstName`) : ""}
            isError={Boolean(errors.firstName)}
            label={t("first_name")}
            placeholder={t("first_name")}
          />
        </div>

        {/* 3. 우편번호 (郵便番号) */}
        <div className="relative col-span-3">
          <Input
            {...register("zip", {
              required: true,
              onChange: onChangeZip,
              onBlur: () => {
                setIsZipSuggestionsVisible(false);
              },
            })}
            autoComplete="off"
            errorMessage={errors.zip ? t(`error.necessary_zip`) : ""}
            inputMode="numeric"
            isError={Boolean(errors.zip)}
            label={t("postal_code")}
            onFocus={() => {
              setIsZipSuggestionsVisible(true);
            }}
            placeholder={t("postal_code")}
          />
          {isZipSuggestionsVisible && zipSuggestions.length !== 0 ? (
            <AddressSuggestions
              // 최대 20개라 목록 안에서 세로 스크롤, 끝에 닿아도 폼 영역으로 스크롤이 넘어가지 않도록 overscroll-contain
              className="max-h-60 overflow-y-auto overscroll-contain"
              onSelect={onSelectZipSuggestion}
              suggestions={zipSuggestions}
            />
          ) : null}
        </div>

        {/* 4. 도도부현 (都道府県) */}
        <div className="col-span-3 flex flex-col gap-1">
          <Select
            as="form"
            className="pc:h-8.75 h-10"
            iconClassName="top-8.5! pc:top-8!"
            id="zoneCode"
            isError={Boolean(errors.zoneCode)}
            label={t("province")}
            options={PREFECTURE_OPTIONS}
            placeholder={t("province")}
            {...register("zoneCode", { required: true, pattern: JAPANESE_REGEX })}
          />
          {errors.zoneCode ? (
            <span className="text-red font-fuzzy-light leading-none">
              {t(errors.zoneCode.type === "pattern" ? `error.invalid_japanese` : `error.necessary_province`)}
            </span>
          ) : null}
        </div>

        {/* 5. 시구정촌 (市区町村) */}
        <div className="col-span-6">
          <Input
            {...register("city", { required: true, pattern: JAPANESE_REGEX })}
            errorMessage={errors.city ? t(errors.city.type === "pattern" ? `error.invalid_japanese` : `error.necessary_city`) : ""}
            isError={Boolean(errors.city)}
            label="市区町村"
            placeholder="市区町村"
          />
        </div>

        {/* 6. 주소 (住所) */}
        <div className="col-span-6">
          <Input
            {...register("address1", { required: true, pattern: JAPANESE_ADDRESS_REGEX })}
            errorMessage={
              errors.address1 ? t(errors.address1.type === "pattern" ? `error.invalid_japanese` : `error.necessary_address1`) : ""
            }
            isError={Boolean(errors.address1)}
            label={t("address1")}
            placeholder={t("address1")}
          />
        </div>

        {/* 7. 건물명, 방 번호 등 (建物名、部屋番号など) */}
        <div className="col-span-6">
          <Input {...register("address2")} label={t("address2")} placeholder={t("address2")} />
        </div>

        {/* 8. 전화번호 (携帯電話) */}
        <div className="col-span-6">
          <Input
            {...register("phoneNumber", { required: true })}
            errorMessage={errors.phoneNumber ? t(`error.necessary_phone`) : ""}
            isError={Boolean(errors.phoneNumber)}
            label={t("phone_number")}
            placeholder={t("phone_number")}
            type="tel"
          />
        </div>
      </div>

      <div className="pc:gap-2.5 pc:pt-4 absolute bottom-0 left-0 col-span-8 flex w-full shrink-0 cursor-pointer flex-col gap-5 bg-white p-4 pt-5">
        {!isDefaultAddress && !isEmptyAddress && (
          <Input
            checked={checkedDefaultAddress}
            id="defaultAddress"
            label={t("address_book.set_as_default_address")}
            name="defaultAddress"
            onChange={() => {
              setCheckedDefaultAddress((prev) => !prev);
            }}
            type="checkbox"
          />
        )}
        <Button as="square" disabled={isLoading} loading={isLoading} type="submit">
          {t("address_book.save")}
        </Button>
      </div>
    </form>
  );
}
